import React, { useMemo } from 'react';
import {
  IFieldFieldsFragment,
  INamedFilterFieldsFragment,
  ITagOptionInput,
  PermissionKey,
  TagOption,
} from '../../../../../../utils/apollo/graphqlSchema';
import EntitySelection from '../../../../../Builder/EntitySelection';
import { createTagOptionInput } from '../../../../../Builder/EntitySelection/utils';
import {
  IEntitySelectionLabelProps,
  ISelectedEntity,
  ISelectEntityHandler,
  ISubsetSelectedEntity,
} from '../../../../../Builder/EntitySelection/types';
import usePermission from '../../../../../../utils/hooks/permissionHooks/usePermission';
import { assertCondition } from '../../../../../../utils/types';
import { useFilterWidgetPropContext } from '../../../../FilterWidgetPropContext';
import { MaybeNil } from '../../../../../../utils/interfaces';
import { isNil } from 'lodash';

interface IProps {
  field: IFieldFieldsFragment | null;
  onChange: (
    newValue: IFieldFieldsFragment | INamedFilterFieldsFragment | null,
  ) => void;
  placeholder: string;
  operandTypeIsField: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  allowNamedFilter: boolean;
  allowField: boolean;
  hasError?: boolean;
  isReadOnly?: boolean;
  rootFieldKey?: MaybeNil<string>;
}

type IFieldFilterSelectedEntity = ISubsetSelectedEntity<
  'Field' | 'NamedFilter'
>;

const FilterWidgetFieldEntity = ({
  field,
  onChange,
  placeholder,
  operandTypeIsField,
  autoFocus,
  disabled,
  allowNamedFilter,
  allowField,
  hasError,
  isReadOnly,
  rootFieldKey,
}: IProps) => {
  const { schemaId, context } = useFilterWidgetPropContext();

  const { checkIsPermitted } = usePermission();
  const isPermittedToReadNamedFilter = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.FilterRead],
    schemaId,
  });

  const handleChange: ISelectEntityHandler = (
    selectedOption: ISelectedEntity,
  ) => {
    assertCondition(
      selectedOption.__typename === 'NamedFilter' ||
        selectedOption.__typename === 'Field',
    );

    onChange(selectedOption);
  };

  const handleReset = () => {
    onChange(null);
  };

  const tags: ITagOptionInput[] = useMemo(() => {
    const result: ITagOptionInput[] = [];

    if (allowField) {
      result.push(
        createTagOptionInput(TagOption.Field, {
          field: {
            events: context.events,
            context: context.fieldContext,
            func: context.func,
            arrayObjectContext: !isNil(rootFieldKey)
              ? { rootField: rootFieldKey }
              : null,
          },
        }),
      );
    }

    if (
      !operandTypeIsField &&
      isPermittedToReadNamedFilter &&
      allowNamedFilter
    ) {
      result.push(createTagOptionInput(TagOption.Filter));
    }

    return result;
  }, [
    allowField,
    allowNamedFilter,
    context.events,
    context.fieldContext,
    context.func,
    isPermittedToReadNamedFilter,
    operandTypeIsField,
    rootFieldKey,
  ]);

  const selectedEntities: IFieldFilterSelectedEntity[] = useMemo(() => {
    if (!field) {
      return [];
    }

    return [field];
  }, [field]);

  const entityLabelProps: IEntitySelectionLabelProps[] = [
    {
      type: 'Field',
      chipLabelVariantPicker: () => (operandTypeIsField ? 'purple' : undefined),
    },
  ];

  return (
    <EntitySelection
      schemaId={schemaId}
      selectedEntities={selectedEntities}
      tags={tags}
      onRemoveEntity={handleReset}
      onSelectEntity={handleChange}
      labelProps={entityLabelProps}
      autoFocus={autoFocus}
      placeholder={placeholder}
      isDisabled={disabled}
      isReadOnly={isReadOnly || tags.length === 0}
      isError={hasError}
    />
  );
};

export default FilterWidgetFieldEntity;
